import { useCallback, useRef } from 'react'

import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { ConversationCommandModal } from './components/ConversationCommandModal'
import messages from './messages'
import { useGlobalCommandView } from './useGlobalCommandView'
import { GlobalCommandModal } from '../../components/GlobalCommandModal'
import { GuidedIntakePromoModal } from '../../components/GuidedIntakePromoModal'
import { GetGlobalSearchResultsPresenter } from '../../features/get-global-search-results/presenters/GetGlobalSearchResultsPresenter'
import { ConversationBootstrapModes } from '../../hooks/useConversationStream'
import { useGlobalSearchHotkey } from '../../hooks/useGlobalSearchHotkey'
import { CartProvider } from '../../providers/CartProvider'
import { CatalogItemCartProvider } from '../../providers/CatalogItemCartProvider'
import { ConversationProvider } from '../../providers/ConversationProvider'
import { useGlobalSearch } from '../../providers/GlobalSearchProvider'

export const GlobalCommandView = () => {
  const intl = useProcurifyIntl()
  const DATA_2061_GUIDED_INTAKE_ORDERS = useFeatureFlag(
    'DATA_2061_GUIDED_INTAKE_ORDERS'
  )
  const { isOpen, open, close } = useGlobalSearch()
  const {
    data,
    states: { isGuidedIntakePromoOpen },
    methods: {
      startConversation,
      resumeConversation,
      endConversation,
      openGuidedIntakePromo,
      closeGuidedIntakePromo,
    },
  } = useGlobalCommandView()
  const searchInputRef = useRef<HTMLInputElement>(null)

  useGlobalSearchHotkey(open)

  const focusSearchInput = () => {
    searchInputRef.current?.focus()
  }

  const handleClose = useCallback(() => {
    endConversation()
    close()
  }, [endConversation, close])

  const handleGuidedIntakePromoOpen = useCallback(() => {
    openGuidedIntakePromo()
    handleClose()
  }, [openGuidedIntakePromo, handleClose])

  const conversationBootstrap = DATA_2061_GUIDED_INTAKE_ORDERS
    ? data.bootstrap
    : null

  if (conversationBootstrap === null) {
    return (
      <>
        <GlobalCommandModal
          isOpen={isOpen}
          onClose={handleClose}
          onEntered={focusSearchInput}
        >
          <GetGlobalSearchResultsPresenter
            key={isOpen ? 'open' : 'closed'}
            inputRef={searchInputRef}
            onClose={handleClose}
            onHandoff={
              DATA_2061_GUIDED_INTAKE_ORDERS ? startConversation : undefined
            }
            showGuidedIntakePromo={!DATA_2061_GUIDED_INTAKE_ORDERS}
            onGuidedIntakePromoClick={handleGuidedIntakePromoOpen}
            onResumeConversation={
              DATA_2061_GUIDED_INTAKE_ORDERS ? resumeConversation : undefined
            }
          />
        </GlobalCommandModal>

        <GuidedIntakePromoModal
          open={isGuidedIntakePromoOpen}
          onClose={closeGuidedIntakePromo}
        />
      </>
    )
  }

  return (
    <ConversationProvider bootstrap={conversationBootstrap}>
      <CatalogItemCartProvider>
        <CartProvider
          conversationLabel={
            conversationBootstrap.mode === ConversationBootstrapModes.NEW
              ? conversationBootstrap.initialQuery ||
                intl.formatMessage(messages.newRequestLabel)
              : null
          }
          onContinue={handleClose}
        >
          <ConversationCommandModal
            isOpen={isOpen}
            onClose={handleClose}
            onEntered={focusSearchInput}
            title={intl.formatMessage(messages.conversationTitle)}
          />
        </CartProvider>
      </CatalogItemCartProvider>
    </ConversationProvider>
  )
}
